import { useEffect, useState } from 'react'
import { Button, Col, Container, Form, Row } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom'
import KConfirm from '../components/KConfirm'
import KLayerNavigation from '../components/KLayerNavigation'
import SubscriptionRequiredNotice from '../components/SubscriptionRequiredNotice'
import { apiRequest, isUserLoggedIn, showKAlert, updateAccessName } from '../helpers'
import { t } from '../locale'
import { PaymentMethod, User } from '../react-app-env'
import { formatPrice } from '../service/currency'
import v from '../validators'
import PhoneInput from 'react-phone-input-2'
import { isSwissDomain } from '../service/currency'

function UserForm(props: any) {
  const navigate = useNavigate()

  const params: any = useParams()
  const userId = params.userId

  const [user, setUser] = useState({} as User)

  const [gender, setGender] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  const [isMain, setIsMain] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [pinCodeEmail, setPinCodeEmail] = useState(!props.edit)
  const [pinCodeSms, setPinCodeSms] = useState(false)

  const [isGenderValid, setIsGenderValid] = useState(true)
  const [isFirstNameValid, setIsFirstNameValid] = useState(true)
  const [isLastNameValid, setIsLastNameValid] = useState(true)
  const [isEmailValid, setIsEmailValid] = useState(true)
  const [isPhoneValid, setIsPhoneValid] = useState(true)
  const [isPincodeOptionsValid, setIsPincodeOptionsValid] = useState(true)

  const domain = isSwissDomain() ? 'ch' : 'de'

  const [confirmShow, setConfirmShow] = useState(false)
  const [pendingUserData, setPendingUserData] = useState<any>(null)

  const [seatPrice, setSeatPrice] = useState<{
    configured?: boolean
    amount?: number
    currency?: string
  }>({})

  const [hasCard, setHasCard] = useState(false)
  const [loadingSeatInfo, setLoadingSeatInfo] = useState(false)

  // Adding a team member requires an active paid subscription - a trial (or a
  // lapsed subscription) is sent to billing first. `subscribed` stays true until
  // the check resolves so the form doesn't flash the notice on every load.
  const [subscribed, setSubscribed] = useState(true)
  const [subscriptionChecked, setSubscriptionChecked] = useState(false)
  const showSubscriptionNotice = !props.edit && subscriptionChecked && !subscribed

  const loadSeatInfo = async () => {
    setLoadingSeatInfo(true)

    try {
      const [subscriptionRes, priceRes] = await Promise.all([
        apiRequest(`${process.env.REACT_APP_API_URL}/subscription/check`, 'POST', {
          subdomain: window.subdomain,
        }),
        apiRequest(`${process.env.REACT_APP_API_URL}/stripe/additional-seat-price`, 'GET'),
      ])

      setSeatPrice(priceRes)

      if (priceRes?.configured && subscriptionRes?.customerId) {
        const res = await apiRequest(
          `${process.env.REACT_APP_API_URL}/stripe/get-payment-methods`,
          'POST',
          {
            customerId: subscriptionRes.customerId,
          }
        )

        const methods: PaymentMethod[] = res.paymentMethods || []
        setHasCard(methods.some(pm => pm.type === 'card'))
      }
    } catch (error) {
      console.error('Failed to load additional seat charge info:', error)
    }

    setLoadingSeatInfo(false)
  }

  function create(userData: any) {
    fetch(`${process.env.REACT_APP_API_URL}/user-admin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    })
      .then(r => r.json())
      .then(r => {
        if (r.ok) {
          navigate('/user')
        } else {
          showKAlert(r.message?.variant ?? 'danger', r.message?.content ?? 'userNotCreated')
        }
      })
      .catch(() => {
        showKAlert('danger', 'userNotCreated')
      })
  }

  function update(userData: any) {
    fetch(`${process.env.REACT_APP_API_URL}/user-admin`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    })
      .then(r => r.json())
      .then(r => {
        if (r.ok) {
          showKAlert('success', 'userUpdated')
          updateAccessName(userData)
          navigate(-1)
        }
      })
  }

  function handleGender(e: any) {
    setGender(e.target.value)
    setIsGenderValid(v.gender(e.target.value))
  }
  function handleFirstName(e: any) {
    setFirstName(e.target.value.trimStart())
    setIsFirstNameValid(v.name(firstName))
  }
  function handleLastName(e: any) {
    setLastName(e.target.value.trimStart())
    setIsLastNameValid(v.name(lastName))
  }
  function handleEmail(e: any) {
    setEmail(e.target.value.trimStart())
    setIsEmailValid(v.email(email))
  }

  function handlePincodeEmail(e: any) {
    setPinCodeEmail(e.target.checked)
    const isValid = e.target.checked || pinCodeSms
    setIsPincodeOptionsValid(isValid)
  }
  function handleSubmit(e: any) {
    e.preventDefault()

    if (!props.edit && !subscribed) {
      showKAlert('danger', 'trialAddUserRequiresSubscription')
      return
    }

    setIsGenderValid(v.gender(gender))
    setIsFirstNameValid(v.name(firstName))
    setIsLastNameValid(v.name(lastName))
    setIsEmailValid(v.email(email))
    setIsPhoneValid(v.phone(phone))
    setIsPincodeOptionsValid(pinCodeEmail || pinCodeSms)

    if (
      v.gender(gender) &&
      v.name(firstName) &&
      v.name(lastName) &&
      v.email(email) &&
      v.phone(phone) &&
      (pinCodeEmail || pinCodeSms)
    ) {
      const userData = {
        _id: user._id,
        gender: gender,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone,
        active: isActive,
        confirmed: isConfirmed,
        permissions: {
          admin: isAdmin,
          main: isMain,
        },
        pinCodeOptions: {
          email: pinCodeEmail,
          sms: pinCodeSms,
        },
        domain: domain,
      }

      const requiresSeatCharge = isAdmin && (!props.edit || !user.permissions?.admin)

      if (requiresSeatCharge && seatPrice.configured) {
        setPendingUserData(userData)
        setConfirmShow(true)
      } else if (props.edit) {
        update(userData)
      } else {
        create(userData)
      }
    }
  }

  useEffect(() => {
    props.edit &&
      fetch(`${process.env.REACT_APP_API_URL}/user-admin/user/${userId}`)
        .then(r => r.json())
        .then(r => {
          if (!!r.user) {
            const user = r.user as User

            setUser(user)

            setIsMain(user.permissions.main)

            setGender(user.gender)
            setIsGenderValid(v.gender(user.gender))

            setFirstName(user.firstName)
            setIsFirstNameValid(v.name(user.firstName))

            setLastName(user.lastName)
            setIsLastNameValid(v.name(user.lastName))

            setEmail(user.email)
            setIsEmailValid(v.email(user.email))

            setPhone(user.phone)
            setIsPhoneValid(v.phone(user.phone))

            setIsActive(user.active)
            setIsConfirmed(user.confirmed)
            setIsAdmin(user.permissions.admin)

            setPinCodeEmail(user.pinCodeOptions.email)
            setPinCodeSms(user.pinCodeOptions.sms)
          }
        })
  }, [userId, props])

  useEffect(() => {
    if (isAdmin && (!props.edit || !user.permissions?.admin)) {
      loadSeatInfo()
    }
  }, [props.edit, isAdmin, user.permissions?.admin])

  useEffect(() => {
    if (props.edit) return

    apiRequest(`${process.env.REACT_APP_API_URL}/subscription/check`, 'POST', {
      subdomain: window.subdomain,
    })
      .then(res => setSubscribed(!!res?.status && res.type === 'subscribed'))
      .catch(error => console.error('Failed to check subscription:', error))
      .finally(() => setSubscriptionChecked(true))
  }, [props.edit])

  const header = (
    <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3'>
      <p className='pe-none text-draft lead fw-bold link-offset-3 link-underline-primary'>
        {props.edit ? t('userEdit') : t('userCreate')}
      </p>
    </div>
  )

  if (showSubscriptionNotice) {
    return (
      <Container>
        <Row>
          <div className='sidebar col-md-3 col-lg-2 p-0'>
            <KLayerNavigation logged={isUserLoggedIn()} />
          </div>
          <main className='col-md-9 ms-sm-auto col-lg-10 px-md-4 pb-5'>
            {header}
            <div className='my-5'>
              <SubscriptionRequiredNotice message='trialAddUserRequiresSubscription' />
            </div>
          </main>
        </Row>
      </Container>
    )
  }

  return (
    <>
      <Container>
        <Row>
          <div className='sidebar col-md-3 col-lg-2 p-0'>
            <KLayerNavigation logged={isUserLoggedIn()} />
          </div>
          <main className='col-md-9 ms-sm-auto col-lg-10 px-md-4 pb-5'>
            {/* Header */}
            {header}

            <div className='col-12'>
              <div className='my-5 md:grid md:grid-cols-3 md:gap-6'>
                <Row>
                  <Col lg={4}>
                    <div className='pe-4 sm:px-0'>
                      <h6 className='text-draft fw-bold'>{t('userInformation')}</h6>
                      <p className='mt-1 text-draft'>{t('updateUserDetails')}</p>
                    </div>
                  </Col>

                  <Col lg={6} xl={5}>
                    <div className='px-4 py-5 bg-white shadow sm:rounded-md'>
                      <Form noValidate onSubmit={handleSubmit}>
                        <Row className='g-4'>
                          {/*Basic fields */}

                          {/* Gender */}
                          <Col md={12}>
                            <p className='fw-medium smaller text-primary mb-1'>{t('Salutation')}</p>
                            <Form.Select
                              id='salutation'
                              value={gender}
                              onChange={handleGender}
                              isInvalid={!isGenderValid}
                            >
                              <option value=''>--</option>
                              <option value='male'>{t('male')}</option>
                              <option value='female'>{t('female')}</option>
                              <option value='other'>{t('other')}</option>
                            </Form.Select>
                          </Col>

                          {/* First name */}
                          <Col md={6}>
                            <p className='fw-medium smaller text-primary mb-1'>{t('firstName')}</p>
                            <Form.Control
                              id='firstName'
                              type='text'
                              value={firstName}
                              onChange={handleFirstName}
                              onKeyUp={handleFirstName}
                              autoComplete='off'
                              required
                              isInvalid={!isFirstNameValid}
                            />
                          </Col>

                          {/* Last name */}
                          <Col md={6}>
                            <p className='fw-medium smaller text-primary mb-1'>{t('lastName')}</p>
                            <Form.Control
                              id='lastName'
                              type='text'
                              value={lastName}
                              onChange={handleLastName}
                              onKeyUp={handleLastName}
                              autoComplete='off'
                              required
                              isInvalid={!isLastNameValid}
                            />
                          </Col>

                          {/* Email */}
                          <Col md={12}>
                            <p className='fw-medium smaller text-primary mb-1'>{t('email')}</p>
                            <Form.Control
                              id='email'
                              type='email'
                              value={email}
                              onChange={handleEmail}
                              onKeyUp={handleEmail}
                              autoComplete='on'
                              required
                              isInvalid={!isEmailValid}
                            />
                          </Col>

                          {/* Phone */}
                          <Col md={12}>
                            <p className='fw-medium smaller text-primary mb-1'>{t('telephone')}</p>
                            <PhoneInput
                              inputClass='form-control bg-white rounded-md w-100'
                              buttonClass='bg-white'
                              country={window.location.hostname.endsWith('.ch') ? 'ch' : 'de'}
                              value={phone}
                              onChange={phone => setPhone(phone)}
                              inputStyle={{ width: '100%' }}
                              enableSearch={true}
                              preferredCountries={['de', 'ch', 'at']}
                              isValid={isPhoneValid}
                            />
                          </Col>

                          {/* Separator */}
                          <Col xs={12}>
                            <hr className='my-3' style={{ opacity: 0.25 }} />
                          </Col>

                          {/*permissions */}

                          {props.edit && (
                            <>
                              {/* Confirmed */}
                              <Col xs={12} md={4}>
                                <p className='fw-medium smaller text-primary mb-1'>
                                  {t('confirmed')}
                                </p>
                                <input
                                  type='checkbox'
                                  id='confirmed'
                                  className='form-check-input ms-1'
                                  checked={isConfirmed}
                                  onChange={e => !isMain && setIsConfirmed(e.target.checked)}
                                />
                              </Col>

                              {/* Active */}
                              <Col xs={12} md={4}>
                                <p className='fw-medium smaller text-primary mb-1'>{t('active')}</p>
                                <input
                                  type='checkbox'
                                  id='active'
                                  className='form-check-input ms-1'
                                  checked={isActive}
                                  onChange={e =>
                                    !user.permissions?.main && setIsActive(e.target.checked)
                                  }
                                />
                              </Col>

                              {/* Admin */}
                              <Col xs={12} md={4}>
                                <p className='fw-medium smaller text-primary mb-1'>{t('admin')}</p>
                                <input
                                  type='checkbox'
                                  id='isAdmin'
                                  className='form-check-input ms-1'
                                  checked={isAdmin}
                                  onChange={e => !isMain && setIsAdmin(e.target.checked)}
                                />
                              </Col>

                              <Col xs={12}>
                                <hr className='my-3' style={{ opacity: 0.25 }} />
                              </Col>
                            </>
                          )}

                          {!props.edit && (
                            <>
                              <Col md={12}>
                                <p className='fw-medium smaller text-primary mb-1'>{t('admin')}</p>
                                <div className='mt-1'>
                                  <input
                                    type='checkbox'
                                    className='form-check-input'
                                    id='isAdmin'
                                    checked={isAdmin}
                                    onChange={e => setIsAdmin(e.target.checked)}
                                  />
                                </div>
                              </Col>

                              <Col xs={12}>
                                <hr className='my-3' style={{ opacity: 0.25 }} />
                              </Col>
                            </>
                          )}

                          {/* PIN Code */}
                          <Col md={12}>
                            <p className='fw-medium smaller text-primary mb-1'>
                              {t('pinCodeOptions')}
                            </p>

                            <label
                              htmlFor='pinCodeEmail'
                              className='form-check-label text-draft me-3'
                            >
                              {t('email')}
                            </label>
                            <input
                              type='checkbox'
                              id='pinCodeEmail'
                              className='form-check-input me-3'
                              checked={pinCodeEmail}
                              onChange={handlePincodeEmail}
                            />

                            {/* <label htmlFor="pinCodeSms" className="form-check-label text-draft me-3">
                                                            SMS
                                                        </label>
                                                        <input
                                                            type="checkbox"
                                                            id="pinCodeSms"
                                                            className="form-check-input"
                                                            checked={pinCodeSms}
                                                            onChange={handlePincodeSms}
                                                        /> */}

                            {!isPincodeOptionsValid && (
                              <div className='text-danger mt-2'>{t('pincodeOptionNote')}</div>
                            )}
                          </Col>

                          {/* Separator */}
                          <Col xs={12}>
                            <hr className='my-3' style={{ opacity: 0.25 }} />
                          </Col>

                          {/*BUTTONS */}

                          <Col xs={6} className='text-start'>
                            <Button
                              variant='warning'
                              className='text-white'
                              onClick={() => navigate('/user')}
                            >
                              « {t('cancel')}
                            </Button>
                          </Col>

                          <Col xs={6} className='text-end'>
                            <Button variant='success' className='text-white' type='submit'>
                              {props.edit ? t('save') : t('create')} »
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </main>
        </Row>
      </Container>

      <KConfirm
        show={confirmShow}
        onConfirm={() => {
          if (!pendingUserData) return

          setConfirmShow(false)
          props.edit ? update(pendingUserData) : create(pendingUserData)
        }}
        onCancel={() => {
          setConfirmShow(false)
          setPendingUserData(null)
        }}
        confirmDisabled={!loadingSeatInfo && !hasCard}
        message={
          <div>
            <p className='mb-2'>
              {t('additionalSeatWarningBefore')}{' '}
              <strong>{formatPrice(((seatPrice.amount ?? 0) / 100).toFixed(2))}</strong>{' '}
              {t('additionalSeatWarningAfter')}
            </p>

            {!hasCard && !loadingSeatInfo && (
              <p className='mb-0 text-danger'>
                {t('noPaymentMethodsAddOne')}{' '}
                <Link to='/user/billing'>{t('addPaymentMethod')}</Link>
              </p>
            )}
          </div>
        }
      />
    </>
  )
}

export default UserForm
